// SessionGate.jsx
// Nada del portal se monta hasta que la sesión está verificada contra Auth0.
//
// auth0-react da isAuthenticated = true con lo que haya en la caché del
// localStorage, aunque el refresh token ya esté muerto (revocado, vencido o con
// la sesión cerrada en otro lado). Sin este control el portal se veía un
// instante y recién después TokenProbe intentaba renovar y mandaba al login.
//
// Montar el router recién acá, además, hace que lea la URL que dejó
// onRedirectCallback (index.js). BrowserRouter no se entera de un
// history.replaceState hecho por fuera: montado desde el principio quedaba en
// la home aunque el usuario viniera de un enlace a /docs/....

import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

// La ruta completa en la que estaba el usuario, para volver a ella después del
// login. Incluye el hash: HashRedirect resuelve los enlaces viejos con "#/".
export const rutaActual = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

// conCredenciales: prompt=login. Se usa cuando el refresh token del portal murió:
// obliga a un login nuevo en Auth0, así la sesión de Auth0 (7 días) y el token
// del portal vuelven a nacer juntos, alineados con los 7 días de la sesión de
// Sisense. Sin eso, el portal podría sobrevivir a la sesión de Auth0 y el iframe
// de Sisense pediría login adentro del dashboard cuando venza la suya.
export function irAlLogin(loginWithRedirect, { conCredenciales = false } = {}) {
  return loginWithRedirect({
    appState: { returnTo: rutaActual() },
    ...(conCredenciales ? { authorizationParams: { prompt: "login" } } : {}),
  });
}

// Errores con los que Auth0 dice que la sesión ya no sirve. Cualquier otro (red
// caída, timeout del candado entre pestañas) es pasajero: mandar al login por
// eso pedía la contraseña con la sesión viva.
const SESION_MUERTA = new Set([
  "login_required",
  "invalid_grant",
  "missing_refresh_token",
  "consent_required",
  "interaction_required",
]);
export const sesionMuerta = (err) => SESION_MUERTA.has(err?.error);

const ESPERAS_MS = [1000, 3000]; // reintentos ante un error pasajero
const esperar = (ms) => new Promise((r) => setTimeout(r, ms));

// /logout (o /#/logout) existe para limpiar un navegador con la sesión rota sin
// obligar a loguearse antes: no se verifica ni se redirige.
const esLogout = () =>
  window.location.pathname === "/logout" || window.location.hash.startsWith("#/logout");

export default function SessionGate({ children }) {
  const { isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  // "verificando" | "ok" | "sin-conexion"
  const [estado, setEstado] = useState("verificando");
  const libre = esLogout();

  useEffect(() => {
    if (libre || isLoading || estado !== "verificando") return;

    if (!isAuthenticated) {
      irAlLogin(loginWithRedirect);
      return;
    }

    let cancelado = false;
    (async () => {
      for (let intento = 0; ; intento++) {
        try {
          // cacheMode "off": un refresh real contra Auth0, no el token de la caché.
          await getAccessTokenSilently({ cacheMode: "off" });
          if (!cancelado) setEstado("ok");
          return;
        } catch (err) {
          if (cancelado) return;
          if (sesionMuerta(err)) {
            irAlLogin(loginWithRedirect, { conCredenciales: true });
            return;
          }
          if (intento >= ESPERAS_MS.length) {
            setEstado("sin-conexion");
            return;
          }
          await esperar(ESPERAS_MS[intento]);
        }
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [libre, isLoading, isAuthenticated, estado, getAccessTokenSilently, loginWithRedirect]);

  if (libre) return children;
  if (estado === "sin-conexion") {
    // Sin contenido del portal: solo el aviso y la forma de reintentar.
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">No pudimos verificar tu sesión</h1>
          <p className="text-sm text-gray-600 mb-6">Revisá tu conexión e intentá de nuevo.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }
  if (isLoading || !isAuthenticated || estado !== "ok") return null;
  return children;
}
